import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { KeyIcon, SwatchIcon, ChevronRightIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/client';
import { useUser } from '../hooks/useAuth';
import { useToast } from '../components/ui/Toast';
import { PainelIris, inputIris, btnPrimario, btnSecundario } from '../components/ui/iris';

/**
 * Tela do próprio usuário pra editar seus dados (nome, email, fone, endereço)
 * e trocar senha. Não exige permissão — qualquer logado acessa. ADMIN edita
 * os próprios dados pela mesma tela (igual operadores).
 */
const PerfilPage: React.FC = () => {
  const { data: user, isLoading } = useUser();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [fone, setFone] = useState('');
  const [endereco, setEndereco] = useState('');

  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');

  const [salvando, setSalvando] = useState<'perfil' | 'senha' | null>(null);

  useEffect(() => {
    if (user) {
      setNome(user.nome || '');
      setEmail(user.email || '');
      setFone(user.fone || '');
      setEndereco(user.endereco || '');
    }
  }, [user]);

  const handleSalvarPerfil = async (e: React.FormEvent) => {
    e.preventDefault();
    setSalvando('perfil');
    try {
      await authApi.updateProfile({ nome, email, fone: fone || undefined, endereco: endereco || undefined });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      toast.sucesso('Perfil atualizado com sucesso.');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      toast.erro(ax.response?.data?.message || 'Erro ao salvar perfil.');
    } finally {
      setSalvando(null);
    }
  };

  const handleTrocarSenha = async (e: React.FormEvent) => {
    e.preventDefault();
    if (novaSenha !== confirmarSenha) {
      toast.erro('Nova senha e confirmação não conferem.');
      return;
    }
    if (novaSenha.length < 6) {
      toast.erro('Nova senha deve ter no mínimo 6 caracteres.');
      return;
    }
    setSalvando('senha');
    try {
      await authApi.changePassword({ senhaAtual, novaSenha });
      setSenhaAtual('');
      setNovaSenha('');
      setConfirmarSenha('');
      toast.sucesso('Senha alterada com sucesso.');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      toast.erro(ax.response?.data?.message || 'Erro ao trocar senha (verifique a senha atual).');
    } finally {
      setSalvando(null);
    }
  };

  if (isLoading) {
    return <div className="p-6 text-sm text-tx-3">Carregando perfil...</div>;
  }
  if (!user) {
    return <div className="p-6 text-sm text-danger">Não foi possível carregar o perfil.</div>;
  }

  const iniciais = (() => {
    const partes = (user.nome || user.login || '?').trim().split(/\s+/);
    if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
    return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
  })();

  return (
    // Padrão Íris das telas Sistema: conteúdo estreito centralizado.
    <div className="max-w-[980px] mx-auto space-y-4">
      {/* Header (mock: sem ícone — título 22px + descrição 13.5px) */}
      <div>
        <h1 className="text-[22px] font-bold text-tx tracking-[-0.02em]">Meu Perfil</h1>
        <p className="text-[13.5px] text-tx-3 mt-0.5">Atualize seus dados pessoais e senha.</p>
      </div>

      {/* Hero de identidade (mesmo padrão da Organização) */}
      <PainelIris>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="h-16 w-16 shrink-0 rounded-iris-lg bg-accent-soft text-accent-text flex items-center justify-center text-xl font-bold select-none">
            {iniciais}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-lg font-bold text-tx tracking-[-0.02em] truncate">{user.nome}</div>
            <div className="text-[13px] text-tx-2 truncate">
              {user.email} · <span className="font-mono">{user.login}</span>
            </div>
          </div>
          <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-iris-sm ${
            user.perfil === 'ADMIN' ? 'bg-accent-soft text-accent-text' : 'bg-surface-2 text-tx-2'
          }`}>
            {user.perfil === 'ADMIN' ? 'Admin' : 'Usuário'}
          </span>
        </div>
      </PainelIris>

      {/* Dados pessoais */}
      <form onSubmit={handleSalvarPerfil}>
        <PainelIris titulo="Dados pessoais" descricao="Como você aparece pra outros usuários e em documentos.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CampoPerfil label="Nome *">
              <input type="text" value={nome} onChange={e => setNome(e.target.value)} required className={`w-full ${inputIris}`} />
            </CampoPerfil>
            <CampoPerfil label="Email *">
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className={`w-full ${inputIris}`} />
            </CampoPerfil>
            <CampoPerfil label="Telefone">
              <input type="text" value={fone} onChange={e => setFone(e.target.value)} className={`w-full ${inputIris}`} />
            </CampoPerfil>
            <CampoPerfil label="Endereço">
              <input type="text" value={endereco} onChange={e => setEndereco(e.target.value)} className={`w-full ${inputIris}`} />
            </CampoPerfil>
          </div>
          <div className="mt-4 flex justify-end">
            <button type="submit" disabled={salvando === 'perfil'} className={btnPrimario}>
              {salvando === 'perfil' && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
              {salvando === 'perfil' ? 'Salvando…' : 'Salvar perfil'}
            </button>
          </div>
        </PainelIris>
      </form>

      {/* Troca de senha */}
      <form onSubmit={handleTrocarSenha}>
        <PainelIris
          titulo={<span className="inline-flex items-center gap-2"><KeyIcon className="h-4 w-4 text-tx-3" /> Trocar senha</span>}
          descricao="Mínimo de 6 caracteres."
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CampoPerfil label="Senha atual *">
              <input type="password" value={senhaAtual} onChange={e => setSenhaAtual(e.target.value)} required autoComplete="current-password" className={`w-full ${inputIris}`} />
            </CampoPerfil>
            <CampoPerfil label="Nova senha *">
              <input type="password" value={novaSenha} onChange={e => setNovaSenha(e.target.value)} required minLength={6} autoComplete="new-password" className={`w-full ${inputIris}`} />
            </CampoPerfil>
            <CampoPerfil label="Confirmar nova *">
              <input type="password" value={confirmarSenha} onChange={e => setConfirmarSenha(e.target.value)} required minLength={6} autoComplete="new-password" className={`w-full ${inputIris}`} />
            </CampoPerfil>
          </div>
          <div className="mt-4 flex justify-end">
            <button type="submit" disabled={salvando === 'senha' || !senhaAtual || !novaSenha} className={btnSecundario}>
              {salvando === 'senha' ? 'Alterando…' : 'Alterar senha'}
            </button>
          </div>
        </PainelIris>
      </form>

      {/* Aparência migrou pra /config (handoff design_handoff_sistema) — link de transição. */}
      <Link
        to="/config#aparencia"
        className="flex items-center gap-3 rounded-iris-lg border border-line bg-surface p-4 hover:bg-surface-2 transition-colors group"
      >
        <SwatchIcon className="h-5 w-5 text-tx-3" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-tx">Aparência</div>
          <div className="text-xs text-tx-3">Tema, densidade e cor de destaque agora ficam em Configurações.</div>
        </div>
        <ChevronRightIcon className="h-4 w-4 text-tx-3 group-hover:text-tx transition-colors" />
      </Link>
    </div>
  );
};

const CampoPerfil: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-tx-2 mb-1">{label}</label>
    {children}
  </div>
);

export default PerfilPage;
